import Button from '@ui/buttons/Button'
import { colors } from '@ui/colors'
import RemoveOrderModal from '@order/modals/Checkout/RemoveOrder/RemoveOrderModal'
import { Preview } from '@order/modals/Summary/SummaryModal'
import useCartState from '@order/hooks/useCartState'
import usePayments from '@order/hooks/usePayments'
import { trpc } from 'api'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

interface Props {
  preview: Preview | null
  setPreview: (preview: Preview | null) => void
  onRemove?: (orderIds: string[]) => void
}

export default function OrderRemove({ preview, setPreview, onRemove }: Props) {
  const orders = useCartState((s) => s.orders)
  // const orders = mock // TODO: remove me later

  const removeOrders = useCartState((s) => s.removeOrders)
  const { t } = useTranslation(['ui'])

  const [removeId, setRemoveId] = useState<string | null>(null)

  const order = preview ? orders.find((o) => o.id === preview.orderId) : null

  const payments = usePayments((s) => s.payments)

  const payment = useMemo(() => {
    if (!order) return null
    const paymentsArr = Object.values(payments)
    const payment = paymentsArr.find((p) => p.orderIds.includes(order.id))
    return payment
  }, [payments, order])

  const linkedOrders = useMemo(() => {
    if (!removeId) return []

    return orders.filter((order) => order.primaryId === removeId)
  }, [orders, removeId])

  const hasPayment = payment && payment.status !== 'draft'

  const toRemove = useMemo(() => {
    if (!order) return []

    return [order.id, ...linkedOrders.map((o) => o.id)]
  }, [order, linkedOrders])

  const cancelMutation = trpc.order.cancel.useMutation({
    onSuccess: () => {
      removeOrders(toRemove)
      setRemoveId(null)
      onRemove?.(toRemove)
    },
  })

  const handleRemove = () => {
    if (!order) return

    const orderIds = order.result?.orders.map((o) => o.id) ?? []

    linkedOrders.forEach((o) => {
      const ids = o.result?.orders.map((o) => o.id)
      if (ids?.length) {
        orderIds.push(...ids)
      }
    })

    cancelMutation.mutate({ orderIds })
  }

  const isPrimaryAndLinked = useMemo(() => {
    return !(order?.primaryId && linkedOrders?.length === 0)
  }, [order, linkedOrders])

  return (
    <>
      {!hasPayment && (
        <Button
          label={t('ui:remove')}
          variant="outlined"
          className="border-red-700"
          overrideColorFront={colors.red[700]}
          colors={[colors.white, colors.red[100]]}
          onPress={() => setRemoveId(preview?.orderId || null)}
        />
      )}

      <RemoveOrderModal
        open={!!removeId}
        isPending={cancelMutation.isPending}
        close={() => setRemoveId(null)}
        onRemove={handleRemove} // TODO: implement remove order (and linked orders)
        isPrimary={isPrimaryAndLinked}
      />
    </>
  )
}
